import { createMedicationSchema } from "@/lib/api/schemas";

function splitCsvLine(line) {
  const out = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"' && line[i + 1] === '"') {
      current += '"';
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (ch === "," && !inQuotes) {
      out.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }
  out.push(current.trim());
  return out;
}

export function parseInventoryCsv(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) {
    return { rows: [], errors: [{ row: 0, message: "CSV must include a header row and at least one data row." }] };
  }
  const headers = splitCsvLine(lines[0]).map((h) => String(h || "").toLowerCase().trim());
  const norm = (s) => String(s || "").toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  const findCol = (aliases) => {
    const aliasSet = aliases.map((a) => norm(a));
    for (let i = 0; i < headers.length; i += 1) {
      const h = norm(headers[i]);
      if (aliasSet.includes(h)) return i;
    }
    return -1;
  };
  const findDoseInText = (name) => {
    const m = String(name || "").match(/(\d+(?:\.\d+)?\s?(?:mg|mcg|g|ml|iu|%)(?:\s*\/\s*\d+(?:\.\d+)?\s?(?:ml|g))?)/i);
    return m ? m[1].trim() : "";
  };
  const parseNumber = (raw, fallback = null) => {
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    const n = Number.parseFloat(String(raw || "").replace(/,/g, "").replace(/[^\d.+-]/g, ""));
    return Number.isFinite(n) ? n : fallback;
  };
  const inferCategory = (explicit, name) => {
    const e = String(explicit || "").trim();
    if (e) return e;
    const nm = String(name || "");
    const sched = nm.match(/\bS\s*([0-8])\b/i);
    if (sched) return `S${sched[1]}`;
    if (/\b(fridge|refrigerated|cold\s*chain|2-8\s*°?c)\b/i.test(nm)) return "Fridge";
    return "";
  };
  const isoDate = (raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    const mmyy = s.match(/^(\d{1,2})[/-](\d{2,4})$/);
    if (mmyy) {
      const month = Number(mmyy[1]);
      let year = Number(mmyy[2]);
      if (year < 100) year += 2000;
      if (month >= 1 && month <= 12) return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;
    }
    return "";
  };

  const idxName = findCol(["article description", "item description", "name", "name 1", "description", "product", "item", "article"]);
  const idxBatch = findCol(["batch_number", "batch", "lot", "batch no"]);
  const idxExpiry = findCol(["expiry_date", "expiry", "exp date", "exp", "best before", "bb"]);
  const idxQty = findCol(["quantity", "qty", "on hand", "available", "stock", "unrestricted"]);
  const idxCategory = findCol(["category", "schedule", "class", "mdse cat", "merch category"]);
  const idxValue = findCol(["bun value unrestricted", "value unrestricted", "line value", "stock value", "value", "price"]);

  if (idxName < 0 || idxQty < 0) {
    return {
      rows: [],
      errors: [{ row: 0, message: "Could not find name/quantity columns. Try including Name/Article Description and Quantity/Unrestricted." }],
    };
  }

  const defaultExpiry = new Date(Date.UTC(new Date().getUTCFullYear() + 1, 11, 1)).toISOString().slice(0, 10);
  const rows = [];
  const errors = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = splitCsvLine(lines[i]);
    const getAt = (idx) => (idx >= 0 ? cells[idx] || "" : "");
    const name = String(getAt(idxName)).trim();
    const quantity = parseNumber(getAt(idxQty), 1);
    if (!name || !Number.isFinite(quantity) || quantity < 0) {
      errors.push({ row: i + 1, message: "Missing or invalid name/quantity." });
      continue;
    }
    const expiryDate = isoDate(getAt(idxExpiry)) || defaultExpiry;
    const rawValue = parseNumber(getAt(idxValue), null);
    const pricePerBox = rawValue != null && quantity > 0 ? Number((rawValue / quantity).toFixed(2)) : null;
    const category = inferCategory(getAt(idxCategory), name);
    const dosage = findDoseInText(name);
    const raw = {
      name,
      batchNumber: String(getAt(idxBatch)).trim() || dosage || undefined,
      expiryDate,
      quantity,
      category: category || undefined,
      pricePerBox,
      dosage,
      unit: "boxes",
      location: undefined,
      notes: undefined,
    };
    const validated = createMedicationSchema.safeParse(raw);
    if (!validated.success) {
      errors.push({
        row: i + 1,
        message: validated.error.issues?.[0]?.message || "Invalid row",
      });
      continue;
    }
    rows.push({
      ...validated.data,
      pricePerBox: raw.pricePerBox,
      dosage: raw.dosage,
      unit: raw.unit,
    });
  }
  return { rows, errors };
}
